import React, {
	type FormEvent,
	useCallback,
	useMemo,
	useState,
	type ReactNode,
	type MouseEvent,
} from 'react';
import Button from '@atlaskit/button';
import { Label } from '@atlaskit/form';
import { ErrorMessage } from '@atlaskit/form/Messages';
import type { TriggerProps, ContentProps } from '@atlaskit/popup';
import { Box, xcss } from '@atlaskit/primitives';
import TextField from '@atlaskit/textfield';
import { type MessageDescriptor, useIntl } from '@atlassian/jira-intl';
import { FixedWidthPopupComponent } from '@atlassian/jira-jql-builder-basic-picker/src/ui/popup-container/index.tsx';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import type { OnSearchWrapper } from '../../../../common/types.tsx';
import { NUMBER_SIZE_LIMIT_WHEN_ENABLED } from '../../../../common/ui/constants.tsx';
import messages from './messages.tsx';
import type { NumberRangeValue, NumberRangeValueKeys } from './types.tsx';
import { getNumberRangePickerErrorMessages, hasNumberRangeValueError } from './utils.tsx';

type Props = {
	isOpen: boolean;
	onClose: () => void;
	renderTrigger: (triggerProps: TriggerProps) => ReactNode;
	value: NumberRangeValue;
	onSearchWrapper?: OnSearchWrapper;
	onChange: (arg1: NumberRangeValue) => void;
};

export const NumberRangePickerPopup = ({
	isOpen,
	onClose,
	renderTrigger,
	value,
	onChange,
}: Props) => {
	const { formatMessage } = useIntl();
	const [unsubmittedValue, setUnsubmittedValue] = useState<NumberRangeValue>(value);

	const [errorStateMessages, setErrorStateMessages] = useState(() =>
		getNumberRangePickerErrorMessages(value),
	);

	const hasError = useMemo(
		() => hasNumberRangeValueError(errorStateMessages),
		[errorStateMessages],
	);

	const handleOnChange = useCallback(
		(index: NumberRangeValueKeys) =>
			({ currentTarget: { value: targetValue } }: FormEvent<HTMLInputElement>) => {
				const newUnsubmittedValue: NumberRangeValue = {
					...unsubmittedValue,
					[index]: targetValue,
				};
				setUnsubmittedValue(newUnsubmittedValue);
				setErrorStateMessages(getNumberRangePickerErrorMessages(newUnsubmittedValue, index));
			},
		[unsubmittedValue],
	);

	const handleOnSubmit = useCallback(
		(e: MouseEvent) => {
			e.preventDefault();
			onChange(unsubmittedValue);
		},
		[onChange, unsubmittedValue],
	);

	const showErrorMessage = useCallback(
		(messageDescriptor: MessageDescriptor) => (
			<Box xcss={errorMessageContainerStyles}>
				<ErrorMessage>
					{messageDescriptor === messages.numberSizeLimitErrorMessage
						? formatMessage(messageDescriptor, {
								maxSize: NUMBER_SIZE_LIMIT_WHEN_ENABLED.toLocaleString(),
							})
						: formatMessage(messageDescriptor)}
				</ErrorMessage>
			</Box>
		),
		[formatMessage],
	);

	const renderContent = useCallback(
		({ setInitialFocusRef }: ContentProps) => (
			<>
				<Box xcss={textFieldGroupContainerStyles}>
					<Box xcss={clauseDropdownContainerStyles}>
						<Label htmlFor="lower-clause-number-range-field">
							{formatMessage(messages.minimumClauseLabel)}
						</Label>
						<TextField
							ref={setInitialFocusRef}
							id="lower-clause-number-range-field"
							onChange={handleOnChange('min')}
							value={unsubmittedValue.min}
							placeholder={formatMessage(messages.numberRangePlaceholderMessage)}
							isInvalid={hasError && errorStateMessages.min !== undefined}
							testId="jql-builder-basic.ui.jql-editor.field.number-range-picker.lower-clause-text-field"
						/>
						{hasError &&
							errorStateMessages.min !== undefined &&
							showErrorMessage(errorStateMessages.min)}
					</Box>
					<Box xcss={clauseDropdownContainerStyles}>
						<Label htmlFor="upper-clause-number-range-field">
							{formatMessage(messages.maximumClauseLabel)}
						</Label>
						<TextField
							id="upper-clause-number-range-field"
							onChange={handleOnChange('max')}
							value={unsubmittedValue.max}
							placeholder={formatMessage(messages.numberRangePlaceholderMessage)}
							isInvalid={hasError && errorStateMessages.max !== undefined}
							testId="jql-builder-basic.ui.jql-editor.field.number-range-picker.upper-clause-text-field"
						/>
						{hasError &&
							errorStateMessages.max !== undefined &&
							showErrorMessage(errorStateMessages.max)}
					</Box>
				</Box>
				<Box xcss={buttonContainerStyles}>
					<Button
						appearance="primary"
						onClick={handleOnSubmit}
						type="submit"
						isDisabled={hasError}
						testId="jql-builder-basic.ui.jql-editor.field.number-range-picker.update-button"
					>
						{formatMessage(messages.updateButtonText)}
					</Button>
				</Box>
			</>
		),
		[
			errorStateMessages,
			formatMessage,
			handleOnChange,
			handleOnSubmit,
			hasError,
			showErrorMessage,
			unsubmittedValue,
		],
	);

	return (
		<Popup
			content={renderContent}
			isOpen={isOpen}
			onClose={onClose}
			placement="bottom-start"
			popupComponent={FixedWidthPopupComponent}
			shouldRenderToParent
			messageId="jql-builder-basic.ui.jql-editor.field.number-range-picker.popup"
			messageType="transactional"
			trigger={renderTrigger}
		/>
	);
};

const textFieldGroupContainerStyles = xcss({
	paddingLeft: 'space.150',
	paddingRight: 'space.150',
});

const clauseDropdownContainerStyles = xcss({
	marginTop: 'space.100',
	marginBottom: 'space.100',
});

const buttonContainerStyles = xcss({
	borderColor: 'color.border',
	borderStyle: 'solid',
	borderWidth: '0',
	borderTopWidth: 'border.width',
	paddingInline: 'space.150',
	paddingBlock: 'space.075',
});

const errorMessageContainerStyles = xcss({
	margin: 'space.050',
});
